/**
 * Build version watcher — detects new deploys and prompts a soft
 * reload. Mounted once at the app root.
 *
 * Polls ``GET /api/version`` every 3 minutes (and on tab focus).
 * When the build hash changes from the one captured at boot, a
 * sticky toast appears in the user's UI language with a "Aggiorna"
 * button that performs a clean reload (busts cache via
 * ``location.reload()`` + explicit cache eviction).
 *
 * Why it exists:
 * Pre-29/05/2026, users had to Ctrl+F5 after every deploy because of
 * the Service Worker + 5-min bundle cache combo. Even after scoping
 * the SW to /vin-scanner only, the browser's HTTP disk cache can
 * keep serving the old bundle for a few minutes. This watcher tells
 * the user explicitly and gives them a 1-click safe reload.
 *
 * 10/06/2026 — Fabrizio req.:
 *   - 30s countdown that auto-reloads (so abandoned tabs eventually
 *     pick up the new build without manual intervention).
 *   - 14-language localisation matching the dealer welcome email
 *     pack (IT/UK/FR/ES/DE/NL/PT/PL/SE/NO/FI/DK/CZ/GR).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { RefreshCw, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 3 * 60 * 1000;   // 3 minutes
const COUNTDOWN_SECONDS = 30;
const STORAGE_DISMISS_KEY = "build_update_dismissed_for";
// We extract `/static/js/main.<HASH>.js` from the served HTML — webpack
// content-hashes the bundle filename so it ONLY changes when the JS
// changes. This is far more reliable than asking the backend (which
// runs in a separate Kubernetes pod and doesn't share the frontend
// build dir, so its `/api/version` falls back to a useless boot-
// timestamp on every restart). Fabrizio reported 11/06/2026 that admin
// users still had the old bundle in cache after deploy because the
// backend-side fingerprint kept the watcher silent.
const BUNDLE_HASH_RE = /\/static\/js\/main\.[a-z0-9]+\.js/i;

// Map ISO country codes (used internally by the portal as "language
// codes") to ISO 639-1 language codes that match our TRANSLATIONS dict.
const COUNTRY_TO_LANG = {
  IT: "it",
  UK: "en", GBZ: "en", IRL: "en",
  FR: "fr", BE: "fr",
  ES: "es",
  DE: "de", CH: "de",
  NL: "nl",
  PT: "pt",
  PL: "pl",
  SE: "sv",
  NO: "no",
  FI: "fi",
  DK: "da",
  CZ: "cs",
  GR: "el",
};

const TRANSLATIONS = {
  it: { title: "Nuova versione disponibile", body: "La piattaforma è stata aggiornata. Ricarica per applicare i miglioramenti.", reload: "Aggiorna", later: "Più tardi", auto: (s) => `Si aggiornerà tra ${s}s se non clicchi nulla`, close: "Chiudi" },
  en: { title: "New version available", body: "The platform has been updated. Reload to apply the improvements.", reload: "Update", later: "Later", auto: (s) => `Auto-reloading in ${s}s unless you act`, close: "Close" },
  fr: { title: "Nouvelle version disponible", body: "La plateforme a été mise à jour. Rechargez pour appliquer les améliorations.", reload: "Mettre à jour", later: "Plus tard", auto: (s) => `Rechargement automatique dans ${s}s sans action`, close: "Fermer" },
  es: { title: "Nueva versión disponible", body: "La plataforma se ha actualizado. Recarga para aplicar las mejoras.", reload: "Actualizar", later: "Más tarde", auto: (s) => `Recarga automática en ${s}s si no actúas`, close: "Cerrar" },
  de: { title: "Neue Version verfügbar", body: "Die Plattform wurde aktualisiert. Neu laden, um die Verbesserungen zu übernehmen.", reload: "Aktualisieren", later: "Später", auto: (s) => `Automatisches Neuladen in ${s}s ohne Aktion`, close: "Schließen" },
  nl: { title: "Nieuwe versie beschikbaar", body: "Het platform is bijgewerkt. Herlaad om de verbeteringen toe te passen.", reload: "Bijwerken", later: "Later", auto: (s) => `Automatisch herladen over ${s}s zonder actie`, close: "Sluiten" },
  pt: { title: "Nova versão disponível", body: "A plataforma foi atualizada. Recarregue para aplicar as melhorias.", reload: "Atualizar", later: "Mais tarde", auto: (s) => `Recarga automática em ${s}s se não agir`, close: "Fechar" },
  pl: { title: "Dostępna nowa wersja", body: "Platforma została zaktualizowana. Załaduj ponownie, aby zastosować ulepszenia.", reload: "Aktualizuj", later: "Później", auto: (s) => `Automatyczne przeładowanie za ${s}s przy braku akcji`, close: "Zamknij" },
  sv: { title: "Ny version tillgänglig", body: "Plattformen har uppdaterats. Ladda om för att tillämpa förbättringarna.", reload: "Uppdatera", later: "Senare", auto: (s) => `Automatisk omladdning om ${s}s utan åtgärd`, close: "Stäng" },
  no: { title: "Ny versjon tilgjengelig", body: "Plattformen er oppdatert. Last inn på nytt for å bruke forbedringene.", reload: "Oppdater", later: "Senere", auto: (s) => `Automatisk omlasting om ${s}s uten handling`, close: "Lukk" },
  fi: { title: "Uusi versio saatavilla", body: "Alusta on päivitetty. Lataa uudelleen ottaaksesi parannukset käyttöön.", reload: "Päivitä", later: "Myöhemmin", auto: (s) => `Automaattinen lataus ${s}s kuluttua ilman toimintaa`, close: "Sulje" },
  da: { title: "Ny version tilgængelig", body: "Platformen er opdateret. Genindlæs for at anvende forbedringerne.", reload: "Opdater", later: "Senere", auto: (s) => `Automatisk genindlæsning om ${s}s uden handling`, close: "Luk" },
  cs: { title: "Je dostupná nová verze", body: "Platforma byla aktualizována. Obnovte stránku pro použití vylepšení.", reload: "Aktualizovat", later: "Později", auto: (s) => `Automatické obnovení za ${s}s bez akce`, close: "Zavřít" },
  el: { title: "Διαθέσιμη νέα έκδοση", body: "Η πλατφόρμα έχει ενημερωθεί. Επαναφορτώστε για να εφαρμόσετε τις βελτιώσεις.", reload: "Ενημέρωση", later: "Αργότερα", auto: (s) => `Αυτόματη επαναφόρτωση σε ${s}δ χωρίς ενέργεια`, close: "Κλείσιμο" },
};

const _detectLang = () => {
  // 0) Customer wallet page → sticky choice of THIS wallet only
  //    (bug 07/07/2026: residual language keys from OTHER customers'
  //    wallets tested in the same browser leaked into admin pages)
  try {
    const walletMatch = (window.location.pathname || "").match(/^\/wallet\/([^/]+)/);
    if (walletMatch) {
      const code = window.localStorage.getItem(`lojack-wallet-uilang:${walletMatch[1]}`);
      if (code && COUNTRY_TO_LANG[code]) return COUNTRY_TO_LANG[code];
    }
  } catch (_) { /* ignore */ }
  // 1) Dealer portal sticky choice
  try {
    const dealerCode = window.localStorage.getItem("dealer_lang");
    if (dealerCode && COUNTRY_TO_LANG[dealerCode]) return COUNTRY_TO_LANG[dealerCode];
  } catch (_) { /* ignore */ }
  // 2) Admin session → admin UI is Italian
  try {
    if (window.localStorage.getItem("lojack_token")) return "it";
  } catch (_) { /* ignore */ }
  // 3) Customer wallet sticky choice — find first matching key
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith("lojack-wallet-uilang:")) {
        const code = window.localStorage.getItem(k);
        if (code && COUNTRY_TO_LANG[code]) return COUNTRY_TO_LANG[code];
      }
    }
  } catch (_) { /* ignore */ }
  // 4) Operational manual sticky choice
  try {
    const manualLang = window.localStorage.getItem("lojack-manual-lang:dealer")
      || window.localStorage.getItem("lojack-manual-lang:seller");
    if (manualLang && COUNTRY_TO_LANG[manualLang]) return COUNTRY_TO_LANG[manualLang];
  } catch (_) { /* ignore */ }
  // 5) Browser language fallback
  try {
    const bl = (navigator.language || "it").slice(0, 2).toLowerCase();
    if (TRANSLATIONS[bl]) return bl;
  } catch (_) { /* ignore */ }
  return "it";
};

// Module-level baseline — survives React StrictMode's mount/unmount/mount
// cycle and remounts on route changes. Set EXACTLY ONCE per page load.
let _baselineBuild = null;
let _baselineFetchInflight = null;

// Test-only escape hatch — never call this in production code. Lets
// the Jest suite reset the cached baseline between tests so each
// scenario starts from a clean slate.
export const __resetBaselineForTests = () => {
  _baselineBuild = null;
  _baselineFetchInflight = null;
};

const _fetchVersion = async () => {
  // Strategy 1 (primary): scrape the served index.html for the current
  // bundle hash. This works across the Kubernetes ingress without
  // requiring backend awareness of the frontend build.
  try {
    // Cache-buster query so HTTP caches / service workers don't keep
    // serving the index.html that points at the OLD bundle.
    const r = await axios.get(`/?_v=${Date.now()}`, {
      timeout: 4000,
      // Avoid axios auto-parsing JSON
      responseType: "text",
      transformResponse: [(d) => d],
    });
    const html = String(r.data || "");
    const m = html.match(BUNDLE_HASH_RE);
    if (m) return `bundle:${m[0]}`;
  } catch {
    /* fall through */
  }
  // Strategy 2 (fallback): the original backend endpoint. Useful
  // during local dev where the dev server serves a non-content-hashed
  // bundle.js and the bundle-scrape strategy can't tell deploys apart.
  try {
    const r = await axios.get(`${API}/version`, { timeout: 4000 });
    return r.data?.build ? `backend:${r.data.build}` : null;
  } catch {
    return null;
  }
};

const _ensureBaseline = async () => {
  if (_baselineBuild) return _baselineBuild;
  if (_baselineFetchInflight) return _baselineFetchInflight;
  _baselineFetchInflight = _fetchVersion().then((b) => {
    if (b && !_baselineBuild) {
      _baselineBuild = b;
      // Fire-and-forget adoption ping. Once per page load (we use a
      // module-level flag) so refreshes and route changes don't
      // double-count. Dedup is also enforced server-side by the
      // (bundle, session, user) unique index.
      _pingAdoption(b).catch(() => {});
    }
    _baselineFetchInflight = null;
    return _baselineBuild;
  });
  return _baselineFetchInflight;
};

// Per-session id so the backend can dedup pings without identifying
// the user. Stable across reloads (sessionStorage), reset on tab close.
const _getSessionId = () => {
  try {
    let sid = sessionStorage.getItem("lojack_session_id");
    if (!sid) {
      sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("lojack_session_id", sid);
    }
    return sid;
  } catch (_) {
    return `mem-${Date.now()}`;
  }
};

// Best-effort identifier for the current user. We look at the same
// localStorage keys used by AuthContext / DealerPortal so we don't
// need to import them here (BuildVersionWatcher is mounted at root).
const _readCurrentUser = () => {
  let email = null, role = "anon";
  try {
    // 1. Admin session
    const adminJwt = localStorage.getItem("lojack_token");
    if (adminJwt) {
      try {
        const payload = JSON.parse(atob(adminJwt.split(".")[1] || ""));
        if (payload?.email) { email = payload.email; role = "admin"; }
      } catch (_) { /* malformed token, ignore */ }
    }
    // 2. Dealer/seller session — DealerPortal stores plain email
    if (!email) {
      const dealerEmail = localStorage.getItem("dealer_email");
      if (dealerEmail) {
        email = dealerEmail;
        role = localStorage.getItem("dealer_role") || "dealer";
      }
    }
  } catch (_) { /* ignore */ }
  return { email, role };
};

const _pingAdoption = async (buildKey) => {
  // Strip the strategy prefix ("bundle:") to keep the stored hash
  // analytics-friendly. Skip pings for the local-dev fallback because
  // the timestamp-based "backend:boot-X" key would pollute prod data.
  if (!buildKey || !buildKey.startsWith("bundle:")) return;
  const bundle_hash = buildKey.slice("bundle:".length);
  const { email, role } = _readCurrentUser();
  try {
    await axios.post(`${API}/build-adoption/ping`, {
      bundle_hash,
      session_id: _getSessionId(),
      user_role: role,
      user_email: email,
    }, { timeout: 4000 });
  } catch (_) { /* telemetry endpoint — never break the app on failure */ }
};

const _doReload = async () => {
  try {
    if (window.caches && caches.keys) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n).catch(() => {})));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
  } catch (_) { /* ignore */ }
  window.location.reload();
};

const BuildVersionWatcher = () => {
  const [newBuild, setNewBuild] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [paused, setPaused] = useState(false);
  const t = TRANSLATIONS[_detectLang()] || TRANSLATIONS.it;
  // Stable ref to the latest "paused" flag for the interval closure
  const pausedRef = useRef(false);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => {
    let alive = true;

    (async () => {
      await _ensureBaseline();
      if (!alive) return;
      const b = _baselineBuild;
      const dismissedFor = window.localStorage.getItem(STORAGE_DISMISS_KEY);
      if (dismissedFor && b && dismissedFor !== b) {
        window.localStorage.removeItem(STORAGE_DISMISS_KEY);
      }
    })();

    const check = async () => {
      await _ensureBaseline();
      if (!_baselineBuild) return;
      const current = await _fetchVersion();
      if (!alive || !current) return;
      if (current !== _baselineBuild) {
        const dismissedFor = window.localStorage.getItem(STORAGE_DISMISS_KEY);
        if (dismissedFor !== current) {
          setNewBuild(current);
          setDismissed(false);
          setCountdown(COUNTDOWN_SECONDS);
        }
      }
    };

    const interval = setInterval(check, POLL_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);

    return () => {
      alive = false;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // 30s countdown → auto-reload. Paused when user hovers the toast
  // (so they have time to read) or has already clicked "Più tardi".
  useEffect(() => {
    if (!newBuild || dismissed) return undefined;
    const tick = setInterval(() => {
      if (pausedRef.current) return;
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(tick);
          _doReload();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [newBuild, dismissed]);

  const handleReload = () => { _doReload(); };

  const handleDismiss = () => {
    if (newBuild) window.localStorage.setItem(STORAGE_DISMISS_KEY, newBuild);
    setDismissed(true);
  };

  if (!newBuild || dismissed) return null;

  const progressPct = Math.max(0, Math.min(100, (countdown / COUNTDOWN_SECONDS) * 100));

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] max-w-sm bg-[#111] border border-lojack-blue-500/40 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4"
      data-testid="build-update-toast"
      role="status"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-lojack-blue-500/15 text-lojack-blue-300 flex items-center justify-center shrink-0">
            <RefreshCw size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm">{t.title}</p>
            <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{t.body}</p>
            <p
              className="text-lojack-blue-300/80 text-[11px] mt-1.5 leading-snug"
              data-testid="build-update-countdown"
            >
              {t.auto(countdown)}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={handleReload}
                className="px-3 py-1.5 bg-lojack-blue-500 hover:bg-lojack-blue-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                data-testid="build-update-reload-btn"
              >
                <RefreshCw size={12} /> {t.reload}
              </button>
              <button
                onClick={handleDismiss}
                className="px-2 py-1.5 text-gray-500 hover:text-gray-300 text-xs"
                data-testid="build-update-dismiss-btn"
              >
                {t.later}
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-600 hover:text-gray-400"
            aria-label={t.close}
          >
            <X size={14} />
          </button>
        </div>
      </div>
      {/* Slim progress bar at the bottom — visual cue for the countdown.
          Hidden while paused so users know hover = pause. */}
      <div
        className="h-1 bg-lojack-blue-500/20"
        data-testid="build-update-progress"
      >
        <div
          className="h-full bg-lojack-blue-500 transition-[width] duration-1000 ease-linear"
          style={{ width: `${progressPct}%`, opacity: paused ? 0.35 : 1 }}
        />
      </div>
    </div>
  );
};

export default BuildVersionWatcher;
